import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Monitor,
  Wifi,
  Code,
  Download,
  Copy,
  CheckCircle2,
  ArrowRight,
  Zap,
  RefreshCw,
  Key,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScreenConnectionPage() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const pythonCode = `"""
Lumina OS - Screen Device Client
Connect your LED screen to the Lumina OS platform
"""

import requests
import time
import json

# Configuration
LUMINA_API_URL = "${BACKEND_URL}/api"
SCREEN_ID = "your-screen-id"  # Get this from Lumina OS dashboard
DEVICE_KEY = "your-device-key"  # Generate a unique key

class LuminaScreenClient:
    def __init__(self, api_url, screen_id, device_key):
        self.api_url = api_url
        self.screen_id = screen_id
        self.device_key = device_key
        self.playlist = []
        self.current_index = 0
    
    def register(self):
        """Register this device with Lumina OS"""
        response = requests.post(
            f"{self.api_url}/screens/register-device",
            json={"screen_id": self.screen_id, "device_key": self.device_key}
        )
        return response.json()
    
    def fetch_playlist(self):
        """Fetch content playlist from Lumina OS"""
        response = requests.get(
            f"{self.api_url}/screens/{self.screen_id}/playlist",
            params={"device_key": self.device_key}
        )
        data = response.json()
        self.playlist = data.get("playlist", [])
        return self.playlist
    
    def heartbeat(self):
        """Send heartbeat to keep screen online"""
        response = requests.post(
            f"{self.api_url}/screens/{self.screen_id}/heartbeat",
            params={"device_key": self.device_key}
        )
        return response.json()
    
    def get_current_content(self):
        """Get current content to display"""
        if not self.playlist:
            return None
        content = self.playlist[self.current_index]
        self.current_index = (self.current_index + 1) % len(self.playlist)
        return content
    
    def run(self, refresh_interval=60):
        """Main display loop"""
        print(f"Starting Lumina Screen Client for {self.screen_id}")
        self.register()
        
        last_fetch = 0
        while True:
            # Refresh playlist periodically
            if time.time() - last_fetch > refresh_interval:
                self.fetch_playlist()
                self.heartbeat()
                last_fetch = time.time()
            
            # Display current content
            content = self.get_current_content()
            if content:
                print(f"Displaying: {content['content_url']}")
                print(f"Duration: {content['duration']}s")
                time.sleep(content['duration'])
            else:
                print("No content to display")
                time.sleep(10)

# Usage
if __name__ == "__main__":
    client = LuminaScreenClient(
        api_url=LUMINA_API_URL,
        screen_id=SCREEN_ID,
        device_key=DEVICE_KEY
    )
    client.run()
`;

  const javascriptCode = `/**
 * Lumina OS - Screen Device Client (JavaScript/Node.js)
 * Connect your LED screen to the Lumina OS platform
 */

const API_URL = '${BACKEND_URL}/api';
const SCREEN_ID = 'your-screen-id';
const DEVICE_KEY = 'your-device-key';

class LuminaScreenClient {
  constructor(apiUrl, screenId, deviceKey) {
    this.apiUrl = apiUrl;
    this.screenId = screenId;
    this.deviceKey = deviceKey;
    this.playlist = [];
    this.currentIndex = 0;
  }

  async register() {
    const response = await fetch(\`\${this.apiUrl}/screens/register-device\`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        screen_id: this.screenId,
        device_key: this.deviceKey
      })
    });
    return response.json();
  }

  async fetchPlaylist() {
    const response = await fetch(
      \`\${this.apiUrl}/screens/\${this.screenId}/playlist?device_key=\${this.deviceKey}\`
    );
    const data = await response.json();
    this.playlist = data.playlist || [];
    return this.playlist;
  }

  async heartbeat() {
    const response = await fetch(
      \`\${this.apiUrl}/screens/\${this.screenId}/heartbeat?device_key=\${this.deviceKey}\`,
      { method: 'POST' }
    );
    return response.json();
  }

  getCurrentContent() {
    if (!this.playlist.length) return null;
    const content = this.playlist[this.currentIndex];
    this.currentIndex = (this.currentIndex + 1) % this.playlist.length;
    return content;
  }

  async run(refreshInterval = 60000) {
    console.log(\`Starting Lumina Screen Client for \${this.screenId}\`);
    await this.register();

    let lastFetch = 0;
    while (true) {
      if (Date.now() - lastFetch > refreshInterval) {
        await this.fetchPlaylist();
        await this.heartbeat();
        lastFetch = Date.now();
      }

      const content = this.getCurrentContent();
      if (content) {
        console.log(\`Displaying: \${content.content_url}\`);
        await new Promise(r => setTimeout(r, content.duration * 1000));
      } else {
        await new Promise(r => setTimeout(r, 10000));
      }
    }
  }
}

// Usage
const client = new LuminaScreenClient(API_URL, SCREEN_ID, DEVICE_KEY);
client.run();
`;

  const curlExamples = `# 1. Register your device
curl -X POST "${BACKEND_URL}/api/screens/register-device" \\
  -H "Content-Type: application/json" \\
  -d '{"screen_id": "your-screen-id", "device_key": "your-device-key"}'

# 2. Fetch playlist
curl "${BACKEND_URL}/api/screens/your-screen-id/playlist?device_key=your-device-key"

# 3. Send heartbeat
curl -X POST "${BACKEND_URL}/api/screens/your-screen-id/heartbeat?device_key=your-device-key"
`;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="screen-connection-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
          Screen Connection Guide
        </h1>
        <p className="text-zinc-400 mt-1">Connect your physical LED screens to Lumina OS</p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
              <Key className="w-6 h-6 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">1. Register Device</h3>
            <p className="text-sm text-zinc-400">Register your screen with a unique device key for authentication</p>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4">
              <RefreshCw className="w-6 h-6 text-emerald-500" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">2. Fetch Playlist</h3>
            <p className="text-sm text-zinc-400">Pull assigned content playlist to display on your screen</p>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 text-purple-500" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">3. Display Content</h3>
            <p className="text-sm text-zinc-400">Loop through content with specified durations and priorities</p>
          </CardContent>
        </Card>
      </div>

      {/* API Endpoints */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            API Endpoints
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div className="flex items-center gap-3 mb-2">
              <Badge className="bg-emerald-500/20 text-emerald-400 border-0">POST</Badge>
              <code className="text-blue-400 text-sm">/api/screens/register-device</code>
            </div>
            <p className="text-sm text-zinc-400">Register a physical screen device with the platform</p>
          </div>

          <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div className="flex items-center gap-3 mb-2">
              <Badge className="bg-blue-500/20 text-blue-400 border-0">GET</Badge>
              <code className="text-blue-400 text-sm">/api/screens/{'{screen_id}'}/playlist</code>
            </div>
            <p className="text-sm text-zinc-400">Fetch the content playlist for a specific screen</p>
          </div>

          <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div className="flex items-center gap-3 mb-2">
              <Badge className="bg-emerald-500/20 text-emerald-400 border-0">POST</Badge>
              <code className="text-blue-400 text-sm">/api/screens/{'{screen_id}'}/heartbeat</code>
            </div>
            <p className="text-sm text-zinc-400">Send heartbeat to report screen status (keeps screen online)</p>
          </div>

          <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div className="flex items-center gap-3 mb-2">
              <Badge className="bg-purple-500/20 text-purple-400 border-0">WS</Badge>
              <code className="text-blue-400 text-sm">/ws/screens</code>
            </div>
            <p className="text-sm text-zinc-400">WebSocket connection for real-time updates (optional)</p>
          </div>
        </CardContent>
      </Card>

      {/* Code Examples */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Implementation Examples
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="python">
            <TabsList className="bg-zinc-800 mb-4">
              <TabsTrigger value="python" className="data-[state=active]:bg-zinc-700">Python</TabsTrigger>
              <TabsTrigger value="javascript" className="data-[state=active]:bg-zinc-700">JavaScript</TabsTrigger>
              <TabsTrigger value="curl" className="data-[state=active]:bg-zinc-700">cURL</TabsTrigger>
            </TabsList>

            <TabsContent value="python">
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 text-zinc-400 hover:text-white"
                  onClick={() => copyToClipboard(pythonCode, 'python')}
                >
                  {copiedCode === 'python' ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <pre className="bg-zinc-900 rounded-lg p-4 overflow-x-auto text-sm">
                  <code className="text-zinc-300 font-mono">{pythonCode}</code>
                </pre>
              </div>
            </TabsContent>

            <TabsContent value="javascript">
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 text-zinc-400 hover:text-white"
                  onClick={() => copyToClipboard(javascriptCode, 'js')}
                >
                  {copiedCode === 'js' ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <pre className="bg-zinc-900 rounded-lg p-4 overflow-x-auto text-sm">
                  <code className="text-zinc-300 font-mono">{javascriptCode}</code>
                </pre>
              </div>
            </TabsContent>

            <TabsContent value="curl">
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 text-zinc-400 hover:text-white"
                  onClick={() => copyToClipboard(curlExamples, 'curl')}
                >
                  {copiedCode === 'curl' ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <pre className="bg-zinc-900 rounded-lg p-4 overflow-x-auto text-sm">
                  <code className="text-zinc-300 font-mono">{curlExamples}</code>
                </pre>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Quick Start */}
      <Card className="glass-card border-zinc-800 border-blue-500/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <Monitor className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Quick Start</h3>
              <ol className="space-y-2 text-sm text-zinc-400">
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-white">1</span>
                  Create a screen in Lumina OS dashboard and note the Screen ID
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-white">2</span>
                  Generate a unique device key (any secure random string)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-white">3</span>
                  Run the client code on your screen device (Raspberry Pi, etc.)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-white">4</span>
                  Assign content to the screen using Content Assignment
                </li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
